//사용할 마케팅 스크립트 목록 
// 1. airbridge
// 2. amplitude
// 3. flarelane
// 4. GA4
// 5. facebookPixel (*전환API 포함, 하기 문의사항 참조)
// 6. Google Tag Manager
// 7. kakaoPixel
// 8. 네이버 전환 페이지 유입경로 [wcs]
// 9. tracelog

import airbridge from 'airbridge-web-sdk-loader';
import * as amplitude from '@amplitude/analytics-browser';
import CryptoJS from 'crypto-js';
import { flareLaneProjectKey, googleADSThreeKey, amplitudeAPIKey, airbridgeTokenKey, whatapKey } from '@/constants/key';
import { loadExternalScript, convertProxyToObject } from '@/utils/common';
import { type RouteLocationNormalizedLoaded } from 'vue-router';
import dayjs from 'dayjs';
import axios from 'axios';
import { SNS } from '@/constants/login';

const memberInfo = ref<any>({});
const traceLogIp = ref('');
const traceLogTime = ref(0);
const traceLogScroll = ref(0);

declare var FlareLane: any;

// 외부 script 로딩 (HEAD)
export const preLoadScript = () => {
  const config = useRuntimeConfig();

  // Kakao
  loadExternalScript(config.public.VITE_KAKAO_DEV_JS);
  loadExternalScript(config.public.VITE_KAKAO_SDK_JS_URL);

  // Apple
  loadExternalScript(config.public.VITE_APPLE_LOGIN_JS);
  // Naver Login
  loadExternalScript(config.public.VITE_NAVER_LOGIN_JS);
  loadExternalScript(`${config.public.VITE_GOOGLE_ADS_JS_URL}${googleADSThreeKey()}`);

  // facebook
  facebookPixelLoadScript();

};

// app.vue 분석 및 sns 스크립트 로딩
export const loadAnalyticsScript = async (): Promise<void> => {
  const config = useRuntimeConfig();

  // 와탭 로드
  await whatapLoad();

  // 1. amplitude init
  const deviceId = await initAmplitude();

  // 2. airbridge init
  airbridgeInit(config.public.VITE_AIRBRIDGE_APP, airbridgeTokenKey(), deviceId);

  // 3. flarelane init
  //initFlareLane();

  // 4. GA4 init
  googleAdsInit(googleADSThreeKey());

  // 5. Google Tag Manager
  googleGTMInit('GTM-N68PD9F');
  googleGTMInit('GTM-5P6MCRS');

  // 네이버 페이 초기화
  initNaverPay();
};

/**
 * whatap script 와탭
 */
export const whatapLoad = (): void => {
  const prjKey = whatapKey();
  console.log('whatapPrjKey::::', prjKey);

  (function (w : any, h : any, _a : any, t : any, a : any, b : any) {
    w = w[a] = w[a] || {
      config: {
        projectAccessKey: prjKey, // 동적으로 가져온 키 적용
        pcode: 43524,
        sampleRate: 100,
        sessionReplaySampleRate: 100,
        sessionReplayMaskAllTexts: false,
        sessionReplayMaskAllInputs: false,
        proxyBaseUrl: "https://rum-ap-northeast-2.whatap-browser-agent.io/",
        ignorePageUrls : [ /^.*test.*$/i , /^.*localhost.*$/i ],
        ignoreResources  : [ /^.*test.*$/i , /^.*localhost.*$/i ],
        collectUserClick : true
      },
    };
    a = h.createElement(_a);
    a.async = true;
    a.src = "https://repo.whatap-browser-agent.io/rum/prod/v1/whatap-browser-agent.js";
    t = h.getElementsByTagName(_a)[0];
    t.parentNode.insertBefore(a, t);
  })(window, document, 'script', 'https://repo.whatap-browser-agent.io/rum/prod/v1/whatap-browser-agent.js', 'WhatapBrowserAgent', '');
};

// Amplitude 초기화
export const initAmplitude = async (): Promise<string | undefined> => {
  console.log('실행순서 ::::::::::::initAmplitude');
  const apiKey = amplitudeAPIKey();

  return new Promise((resolve) => {
    try {
      amplitude.init(apiKey, {
        defaultTracking: {
          pageViews: true,
          sessions: true,
          formInteractions: true,
          fileDownloads: true,
        },
      });

      const interval = setInterval(() => {
        const deviceId = amplitude.getDeviceId();
        if (deviceId) {
          console.log('amplitudeDeviceId (after init):::::::::::', deviceId);
          clearInterval(interval);
          resolve(deviceId); // deviceId 반환
        }
      }, 100);

      setTimeout(() => {
        clearInterval(interval);
        console.warn('amplitude deviceId not ready after timeout:::::::::::::::::');
        resolve(undefined);
      }, 3000);
    } catch (error) {
      console.error('amplitude init error:::::::::::::::::', error);
      resolve(undefined);
    }
  });
};

// FlareLane 초기화
export const initFlareLane = async (): Promise<boolean> => {
  console.log('initFlareLane플레어레인init:::::');

  if (window.flarelaneInit) return true;

  const flarelaneProjectId = flareLaneProjectKey();

  //flareLane 요소를 찾을때 잠깐 기다리기
  const loadWithRetry = async (retries = 2): Promise<boolean> => {
    for (let i = 0; i < retries; i++) {
      const result = await loadExternalScript('https://cdn.flarelane.com/WebSDK.js', 1, false);

      if (!result) continue;

      const flareLane = (window as any).FlareLane;
      if (flareLane) return true;

      await new Promise(res => setTimeout(res, 300)); // 잠깐 기다리기
    }
    return false;
  };

  const loaded = await loadWithRetry();
  if (!loaded) {
    console.error("❌ FlareLane script failed to load or FlareLane not defined");
    return false;
  }

  const flareLane = (window as any).FlareLane;

  flareLane.initialize?.({ projectId: flarelaneProjectId });

  try {
    flareLane.setIsSubscribed?.(true, (isSubscribed: boolean) => {
      console.log('✅ Subscription status:', isSubscribed);
    });

    flareLane.getDeviceId?.((deviceId: string | null) => {
      console.log('✅ Device ID:', deviceId);
    });

    window.flarelaneInit = true;
    console.log('✅ FlareLane init 완료');
  } catch (e) {
    console.error("❌ FlareLane error during setup:", e);
  }

  return window.flarelaneInit;
  /*
  const script = document.createElement('script');
  script.src = "https://cdn.flarelane.com/WebSDK.js";
  script.onload = () => {
    FlareLane.initialize({
      projectId: flarelaneProjectId,
    });

    try {
      const callback = (isSubscribed: boolean) => {
        console.log('Subscription status:', isSubscribed);
      };
      FlareLane.setIsSubscribed(true, callback);

      FlareLane.getDeviceId((deviceId: string | null) => {
        console.log('Device ID:::::', deviceId);
      });

      window.flarelaneInit = true;
      console.log('flarelane init');
    } catch (e) {
      console.error("FlareLane error:", e);
    }
  };

  script.onerror = () => {
    console.error("Failed to load FlareLane script");
  }
  document.head.appendChild(script);
  */
};

/**
 * 네이버 페이 초기화
 * - postscribe 패키지 사용시 SSR 페이지에서 오류가 발생하여 아래와 같이 구현함
 * - https://pay.naver.com/customer/js/naverPayButton.js 내부에 document.write()를 사용하고 있어
 *   js와 css 파일을 직접 로딩 처리함
 */
export const initNaverPay = (): void => {
  const config = useRuntimeConfig();
  const rand = Math.round(+new Date() / 3600000);

  // script 생성
  const script = document.createElement('script');
  script.src = `${config.public.VITE_NAVER_PAY_BUTTON_URL}?site_preference=normal&${rand}`;
  script.type = 'text/javascript';
  script.onload = () => {
    document.dispatchEvent(new CustomEvent('NaverPayButtonLoad'));
  };

  // link 생성
  const link = document.createElement('link');
  link.id = 'NAVER_PAY_STYLE';
  link.type = 'text/css';
  link.rel = 'stylesheet';
  link.href = `https://img.pay.naver.net/static/css/button/button2.css?${rand}`;

  document.body.appendChild(script);
  document.body.appendChild(link);
};

// 네이버 웹마스터 도구(연관채널)
export const naverAssociatedChannel = (): void => {
  const url = useRequestURL()
  const baseUrl = `${url.origin}`
  useHead({
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "http://schema.org",
          "@type": "Person",
          "name": "Desimone",
          "url": baseUrl,
          "sameAs": [
            "https://www.instagram.com/ttobakcare.official/",
            "https://www.facebook.com/DeSimoneProbiotics",
            "https://blog.naver.com/desimone45",
            "https://www.youtube.com/channel/UCzrXr--W_3_b05oW2GquG0A",
            "https://brand.naver.com/desimone"
          ]
        })
      }
    ]
  });
};

export const naverPageInflow = async (
    status: string, 
    totalPrice : any,
  ): Promise<void> => {

  const config = useRuntimeConfig();

  if (typeof window.wcs === 'undefined') {
    const result = await loadExternalScript(config.public.VITE_NAVER_WCS_JS_URL);
    if (!result) return;
  }

  try {
    if (typeof window.wcs_add === 'undefined') window.wcs_add = {};

    window.wcs_add = { wa: 's_93b4d292671' };

    if (typeof window._nasa === 'undefined') window._nasa = {};

    // TODO : 회원가입, 장바구니, 구매완료 페이지 전환시 수정필요
    if (status === 'memberJoinComplete') {
      // 회원가입
      window._nasa["cnv"] = window.wcs.cnv("2", "1");
    } else if (status === 'orderCart') {
      // 장바구니
      window._nasa["cnv"] = window.wcs.cnv("3", "1");

    } else if (status === 'orderComplete' && totalPrice) {
      // 결제완료
      window._nasa["cnv"] = window.wcs.cnv("1", totalPrice);
     
    }

    // 홈페이지 진입
    window.wcs.inflow("ttobakcare.com");
    window.wcs_do(window._nasa);
    window._nasa = {};
  } catch (error) {
    console.error('naver wcs error', error);
  }
};

// airbridge init
export const airbridgeInit = (
  appName: string,
  token: string,
  amplitudeDeviceId?: string
): void => {

  if (window.airbridgeInit) return;

  let deviceUUID = undefined;
  let packageName = undefined;
  let isLimitAdTracking = undefined;
  let appVersion = undefined;

  if (window.AirbridgeDeviceInfo) {
    try {
      deviceUUID = window.AirbridgeDeviceInfo.getDeviceUUID();
      packageName = window.AirbridgeDeviceInfo.getPackageName();
      isLimitAdTracking = window.AirbridgeDeviceInfo.isLimitAdTracking();
      appVersion = window.AirbridgeDeviceInfo.getAppVersion();
    } catch (e) {
      console.error('airbridge get error', e);
    }
  }

  try {
    if (amplitudeDeviceId) {
      console.log('amplitudeDeviceId:::::::::::::222222', amplitudeDeviceId);
    }
    //APP & Browser
    const commonConfig = {
      app: appName,
      webToken: token,
      cookieWindow: 30,
      utmParsing: true,
      autoStartTrackingEnabled: false,
      useProtectedAttributionWindow: true,
      protectedAttributionWindowInMinutes: 4320,
    };

    if (
      typeof deviceUUID === 'string' &&
      typeof packageName === 'string' &&
      typeof isLimitAdTracking === 'boolean' &&
      typeof appVersion === 'string'
    ) {
      // APP
      airbridge.init({
        ...commonConfig,
        mobileApp: {
          deviceUUID,
          packageName,
          limitAdTracking: isLimitAdTracking,
          appVersion,
        },
      });
    } else {
      // Browser
      airbridge.init(commonConfig);
    }

    if (amplitudeDeviceId) {
      airbridge.setDeviceAlias('amplitude_device_id', amplitudeDeviceId);
    }

    airbridge.startTracking();
    window.airbridgeInit = true;
  } catch (e) {
    if (e instanceof Error) console.log('airbridge init error', e.message);
  }
};

// airbridge send
export const airbridgeSendEvent = async (category: string, label: string | null = null, action: string | null = null, value: number | null = null, semanticAttributes: Record<string, any> | null = null, customAttributes: Record<string, any> | null = null): Promise<void> => {
  const config = useRuntimeConfig();
  const envType = config.public.VITE_ENV_TYPE;
  const attribute = {} as any;

  if (label) {
    attribute.label = label;
  }
  if (action) {
    attribute.action = action;
  }
  if (value) {
    attribute.value = value;
  }
  if (semanticAttributes) {
    attribute.semanticAttributes = semanticAttributes;
  }
  if (customAttributes) {
    attribute.customAttributes = customAttributes;
  }

  try {
    if (envType === 'production') {
      const ipRes = await $fetch("/api/get-ip");
      const userIp = ipRes?.ip ?? '';
      const blockedIp = ['61.74.175.185', '61.74.175.186'];
      if (blockedIp.includes(userIp)) {
        console.log('Blocked IP: ', userIp);
        return;
      }
      airbridge.events.send(category, attribute);
    } else {
      airbridge.events.send(category, attribute);
    }
  } catch (error) {
    console.error('airbridge error', error);
  }
};

export const airbridgeSetFieldUser = (userId: string): void => {
  airbridge.setUserID(userId);
};

export const airbridgeclearUser = (): void => {
  airbridge.clearUser();
};

// google Ads init
export const googleAdsInit = (code: string): void => {
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]): void {
    window.dataLayer.push(args);
  }

  gtag('js', new Date());
  gtag('config', code);
};

// Google Ads Event
export const googleAdsEvent = (data: Record<string, any>, eventName: string = 'conversion'): void => {
  window.gtag('event', eventName, data);
  console.log('google ads event');
};


// google gtm init
export const googleGTMInit = (code: string): void => {
  (function (w: Window, d: Document, s: string, l: string, i: string) {
    (w as any)[l] = (w as any)[l] || [];
    (w as any)[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    // script 태그 생성 및 삽입
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s) as HTMLScriptElement;
    const dl = l !== 'dataLayer' ? '&l=' + l : '';

    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode?.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', code);
};

// google gtm 이벤트 전송
export const googleGTMSend = (to: RouteLocationNormalizedLoaded | null = null): void => {
  const route = useRoute();

  const pagePath = to ? to.fullPath : route.fullPath;
  const pageTitle = to ? to.meta.title : route.meta.title;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'Pageview',
    pagePath,
    pageTitle,
  });
};

export const gtag_report_conversion = (url = window.location.href) => {
  var callback = function () {
    if (typeof (url) != 'undefined') {
      // window.location = url;
    }
  }
  return callback
};

// Facebook Pixel Code
export const facebookPixelLoadScript = async (): Promise<void> => {

  // 이미 fbq가 존재하면 중복 삽입 방지
  if (typeof window.fbq === 'function') {
    console.log('fbq already loaded');
    return;
  }

  const script = document.createElement('script');
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
  `;
  document.head.appendChild(script);
};

// Facebook Meta Pixel Code >> noscript
export const facebookPixelLoadNoScript = (): void => {
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `
    <img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=704488810561822&ev=PageView&noscript=1" />
    <img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=2665198720263392&ev=PageView&noscript=1" />
    <img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=5645969855487741&ev=PageView&noscript=1" />
  `;
  document.body.appendChild(noscript);
};

// facebook pixel init
export const facebookPixelInit = (userData: (Record<string, any> | null) = null): void => {
  if (window.facebookPixelInit) return;

  const PIXEL_ID = ['2665198720263392', '704488810561822', '5645969855487741'];

  try {
    if (userData) {
      PIXEL_ID.forEach(ele => {
        window.fbq('init', ele, {
          em: userData['em'] || '',
          fn: userData['fn'] || '',
          ln: userData['ln'] || '',
          ph: userData['ph'] || '',
          external_id: userData['external_id'] || '',
          ge: userData['ge'] || '',
          db: userData['db'] || '',
          ct: userData['ct'] || '',
          st: userData['st'] || '',
          zp: userData['zp'] || '',
          fbc: userData['fbc'] || '',
          fbp: userData['fbp'] || '',
        });
      });
    } else {
      PIXEL_ID.forEach(ele => {
        window.fbq('init', ele);
      });
    }
    window.fbq('track', 'PageView');
    console.log('facebook pixel init', userData);
    window.facebookPixelInit = true;
  } catch (error) {
    console.error('Facebook Pixel initialization failed:', error);
  }
};

export const facebookPixelForUser = async (): Promise<void> => {
  const memberStore = useMemberStore();
  
  if (memberStore.memberSeq) {
    const userInfo = {
      em: memberStore.email ? CryptoJS.SHA256(memberStore.email).toString(CryptoJS.enc.Hex) : "",
      ph: memberStore.mobilePhone ? CryptoJS.SHA256(memberStore.mobilePhone).toString(CryptoJS.enc.Hex) : "",
      ge: CryptoJS.SHA256(memberStore.sex.toLowerCase()).toString(CryptoJS.enc.Hex),
      db: memberStore.birthday ? CryptoJS.SHA256(memberStore.birthday.replace('-', '')).toString(CryptoJS.enc.Hex) : "",
      zp: memberStore.zipCode ? CryptoJS.SHA256(memberStore.zipCode).toString(CryptoJS.enc.Hex) : "",
      country: CryptoJS.SHA256("KR").toString(CryptoJS.enc.Hex),
      fbc: useNuxtApp().$getCookie('_fbc'),
      fbp: useNuxtApp().$getCookie("fbp"),
    };
    facebookPixelInit(userInfo);
  } else {
    facebookPixelInit();
  }
};

// kakao pixel 추적
export const kakaoPixel = async (status: string, info: (Record<string, any> | null) = null): Promise<void> => {
  const config = useRuntimeConfig();

  if (typeof window.kakaoPixel === 'undefined') {
    const result = await loadExternalScript(config.public.VITE_KAKAO_PIXEL_JS_URL);
    if (!result) return;
  }

  try {
    if (status === 'memberJoinComplete') {
      // 회원가입
      window.kakaoPixel('9181702155002397674').pageView();
      window.kakaoPixel('9181702155002397674').completeRegistration('DF');
    } else if (status === 'goodsView') {
      // 제품상세
      if (info) window.kakaoPixel('9181702155002397674').viewContent({ id: info.id });
    } else if (status === 'addCart') {
      // 장바구니 추가
      if (info) window.kakaoPixel('9181702155002397674').addToCart({ id: info.id });
    } else if (status === 'viewCart') {
      // 장바구니이동
      window.kakaoPixel('9181702155002397674').viewCart('DF');
    } else if (status === 'orderComplete') {
      if (info) {
        const convertedKakaoItems = info.kakaoItmes.map((item: unknown) => convertProxyToObject(item));
        // 구매완료
        window.kakaoPixel('9181702155002397674').purchase({
          total_quantity: String(info.totalEa),
          total_price: String(info.totalPrice),
          currency: "KRW",
          products: convertedKakaoItems
        })
      }
    } else {
      window.kakaoPixel('9181702155002397674').pageView('DF');
    }
  } catch (error) {
    console.error('kakao pixel error');
  }
};

// Facebook Pixel product event
export const facebookPixelProductEvent = (target: string, targetValue: string, info: Record<string, any>, contentsList: Record<string, any> | null = null): void => {
  /**
   * - 상세페이지 : traget = 'track', targetValue, ViewContent
   * - 장바구니  : target = 'track', targetValue = 'AddToCart'
   * - 네이버페이 : target = 'trackCustom', targetValue = 'Purchase_NPay'
   */
  window.fbq(target, targetValue, {
    content_ids: contentsList,   // [{id: id , quantity : quantity} , {id: id , quantity : quantity}...]
    content_type: 'product',
    value: info.salePrice.toFixed(2), // toFixed(2) : 소수점 2번째 자리까지 표현
    currency: 'KRW',
    content_name: info.goodsName
  });
};

// Facebook Pixel Event
export const facebookPixelEvent = (target: string, targetValue: string): void => {
  window.fbq(target, targetValue);
};

// Amplitude Event Send
export const amplitudeSendEvent = (eventName: string, eventProps?: Record<string, any>, userProps: Record<string, any> | null = null): void => {
  try {
    if (userProps && Object.keys(userProps).length > 0) {
      const identify = new amplitude.Identify();

      for (const key in userProps) {
        identify.set(key, convertArrayToString(userProps[key]));
      }
      amplitude.identify(identify);
    }

    amplitude.track(eventName, eventProps);

    console.log('amplitude event send');
  } catch (error) {
    console.error('amplitude event send error', error);
  }
};

// Amplitude 사용자 ID 설정
export const amplitudeSetUserId = (userId: string): void => {
  amplitude.setUserId(userId);
};

// Amplitude 사용자 정보 초기화
export const amplitudeClearUser = (): void => {
  amplitude.setUserId(undefined);
  amplitude.reset();
};

// Amplitude Revenue 이벤트 전송
export const amplitudeSendRevenue = (productId: string, price: number, quantity: number, revenue: number) => {
  try {
    const event = new amplitude.Revenue()
      .setProductId(productId)
      .setPrice(price)
      .setQuantity(quantity)
      .setRevenue(revenue)
    amplitude.revenue(event);
  } catch (e) {
    console.error("amplitudeSendRevenue::", e);
  }
}

// FlareLane init 실행 함수 (이벤트 전 init먼저 실행시킴)
export const withFlareLane = async (
  action: (flareLane: any) => Promise<void>
): Promise<void> => {
  console.log('withFlareLane ::: 실행');

  if (!window.flarelaneInit) {
    const initResult = await initFlareLane();
    if (!initResult) {
      return;
    }
  } 

  const flareLane = (window as any).FlareLane;

  try {
    await action(flareLane);
  } catch (err) {
  }

  console.log('withFlareLane ::: 종료');
};


// FlareLane event
export const flareLaneTrackEvent = async (eventName: string, eventProps: Record<string, any>): Promise<void> => {
  //init 정상처리 되었을 때 이벤트 전송
  await withFlareLane(async (flareLane) => {
    await flareLane.trackEvent(eventName, eventProps);
  });
};

// FlareLane 사용자 ID 설정
export const flareLaneSetUserId = async (userId: number | null): Promise<void> => {
  //init 정상처리 되었을 때 이벤트 전송
  await withFlareLane(async (flareLane) => {
    await flareLane.setUserId(userId?.toString() ?? null);
  });
};

// FlareLane 태그 설정
export const flareLaneSetTag = async (userProps: Record<string, any>): Promise<void> => {
  //init 정상처리 되었을 때 이벤트 전송
  await withFlareLane(async (flareLane) => {
    await flareLane.setTags(userProps);
  });
};

// FlareLane 유저 속성 설정
export const flareLaneSetUserAttributes = async (userProfile: Record<string, any>): Promise<void> => {
  //init 정상처리 되었을 때 이벤트 전송
  await withFlareLane(async (flareLane) => {
    await flareLane.setUserAttributes(userProfile);
  });
};

// FlareLane 인앱 노출
export const flareLaneDisplayInApp = async (pageGroup: string): Promise<void> => {
  await withFlareLane(async (flareLane) => {
    await flareLane.displayInApp(pageGroup);
  });
};

//ip가져오기
export const callClientIp = async () => {
  try {
    const { ip } = await $fetch<{ip: string}>("/api/get-ip");
    if (ip) {
      //const userIpCookie = useCookie("USER_IP");
      //userIpCookie.value = ip;
      useNuxtApp().$setCookie('USER_IP', ip);
    } else {
      console.log("callClientIp 실패:::::");
    }
  } catch (error) {
    console.error("callClientIp error:::::", error);
  }
};

/**
   * 추적용 로그이벤트 시작
   * 파라미터 : 현재 url, 이전 url, ip주소, 사용자아이디, 스크롤위치 퍼센트, 페이지에 머무른 시간, poc
   * -- 조건 
   * 1. 페이지 진입시 호출
   * 2. 5초마다 호출
   * 3. 상품상세, 이벤트 페이지만 스크롤 이동시 api 호출한다 (페이지 컨텐츠 영역의 스크롤위치가 1~30%, 31~60%, 61%이상 범위에서만 호출)
   * 4. 상품상세, 이벤트페이지의 경우 스크롤시 페이지에 머문시간을 0으로 리셋한다
   * -- 사용방법
   * 스크롤이벤트를 사용하고자 하는 페이지에서만 헤더와 푸터를 제외한 컨텐츠영역에 아이디를 부여한다 id="traceLogTarget"
 */
export const makeBrowserCookie = (): void => {
  const { $getCookie, $setCookie } = useNuxtApp()
  const BrowserCookie = $getCookie('BrowserCookie');

  if (!BrowserCookie) {
    const today = dayjs(new Date()).format('YYYYMMDDHHmmss'); 
    let randomNum = Math.floor(Math.random() * (99999 - 1) + 1).toString();

    randomNum = randomNum.padStart(5, '0');

    const date = new Date();
    date.setDate(date.getDate() + 30);

    // 브라우저 쿠키 set
    const returnCookie = today + randomNum;
    //BrowserCookie.value = returnCookie;
    $setCookie('BrowserCookie', returnCookie);
  } 
};

export const callTraceLogEvent = async () => {
  await callClientIp();

  // 쿠키에서 클라이언트 IP 가져오기
  //const userIpCookie = useCookie("USER_IP");
  const clientIP = useNuxtApp().$getCookie('USER_IP');
  
  if (clientIP) {
    traceLogIp.value = clientIP;
  };

  // 페이지 진입 시 호출
  apiCallTraceLog(true);

  // 스크롤 이벤트 처리
  traceLogScrollEvent();
};

export const apiCallTraceLog = async (isReset = false, nowUrl = window.location.href, setReferrerUrl = '') => {
  const memberStore = useMemberStore();
  
  try {
    const BrowserCookie = useNuxtApp().$getCookie('BrowserCookie');

    // 시간 업데이트
    traceLogTime.value = isReset ? 0 : (Number(traceLogTime.value) || 0) + 5; // 숫자로 변환

    // Referrer URL 설정
    let referrerUrl = document.referrer;
    if (setReferrerUrl) {
      referrerUrl = setReferrerUrl;
    } else if (window.history.state?.back) {
      referrerUrl = `${checkURL()}${window.history.state.back}`;
    }

    // TraceLog 데이터 구성
    const data = {
      nowUrl,
      referrerUrl,
      poc: 'mw',
      ipAddress: traceLogIp.value,
      scrollPercent: traceLogScroll.value,
      time: traceLogTime.value,
      memberId: memberStore.memberId, 
      BrowserCookie,
    };

    // API 호출
    const { data: result } = await useNuxtApp().$sendPost({
      url: import.meta.env.VITE_TRACELOG_API_URL,
      data,
      showDefaultLoading: false
    });

    console.log('TRACELOG Result', result);
    
  } catch (error) {
    console.error('Error in apiCallTraceLog:', error);
  }
};

export const traceLogScrollEvent = () => {
  let interval: NodeJS.Timeout | null = null;
  let debounceTimer: NodeJS.Timeout | null = null;

  const handleScroll = () => {
    const target = document.getElementById("traceLogTarget");
    if (!target) return;

    const scrollPercent = Math.floor(((window.scrollY - target.offsetTop) / target.offsetHeight) * 100);
    traceLogScroll.value = scrollPercent >= 1 ? (scrollPercent <= 30 ? 1 : scrollPercent <= 60 ? 3 : 5) : 0;

    if (traceLogScroll.value > 0) {
      traceLogTime.value = 0;

      if (interval) clearInterval(interval);

      setTimeout(() => {
        apiCallTraceLog(true);
        interval = setInterval(apiCallTraceLog, 5000);
      }, 1000);
    } else if (interval) {
      clearInterval(interval);
      interval = null;
    }
  };

  const debouncedHandleScroll = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(handleScroll, 1000);
  };

  window.removeEventListener("scroll", debouncedHandleScroll);
  window.addEventListener("scroll", debouncedHandleScroll);

  return () => {
    window.removeEventListener("scroll", debouncedHandleScroll);
    if (interval) clearInterval(interval);
  };
};

//==================================================================
//  마케팅 이벤트 (상품상세 view page)
//==================================================================
const amplitudeInfo: Record<string, any> = {}
const goodsInfo: Record<string, any> = {}
/**
 * 상품상세 초기화
 * @param goodsInfo 
 */
export const makeModuleItemsDetailInit = async (goods: Record<string, any>): Promise<Record<string, any>> => {
  const router = useRouter();
  const adInfo: Record<string, any> = {}

  Object.assign(goodsInfo, goods)

  //기본적인 상품 정보
  Object.assign(adInfo, {
    id: goodsInfo.goodsBasicInfo?.goodsSeq, //상품번호
    goodsName: goodsInfo.goodsBasicInfo?.goodsName, //상품명
    price: goodsInfo.goodsBasicInfo?.regularPrice, //정가
    salePrice: goodsInfo.goodsBasicInfo?.salePrice, //판매가
    quantity: 1,
    soldOut: goodsInfo.goodsBasicInfo?.soldOut ? 'Y' : 'N', //솔드아웃 여부
    imageUrl: goodsInfo.goodsBasicInfo?.goodsImageList?.[0]?.detailEnlargeImageEntity?.localUrl,
    topCategory: goodsInfo.goodsBasicInfo?.brandGroup
  })

  adInfo.moduleInfo = setModuleInfo(true);

  // 앰플리튜드 상품 정보 넘기기
  Object.assign(amplitudeInfo, {
    id: goodsInfo.goodsBasicInfo?.goodsSeq,
    name: goodsInfo.goodsBasicInfo?.goodsName,
    code: goodsInfo.goodsBasicInfo?.nutrientAlarmCode || '', //수정되어야함
    type: goodsInfo.goodsBasicInfo?.goodsName.includes('체험팩') ? '체험팩' :  (goodsInfo.goodsBasicInfo?.goodsType === 'NORMAL' ? '단품' : '정기구독'),
    brandName: goodsInfo.goodsBasicInfo?.brandGroup,
    discountRate: goodsInfo.goodsBasicInfo?.salePercent,
    price: goodsInfo.goodsBasicInfo?.regularPrice,
    discountPrice: goodsInfo.goodsBasicInfo?.salePrice,
    reviewCount: goodsInfo.goodsBasicInfo?.reviewCnt,
  })

  if (goodsInfo.goodsBasicInfo?.goodsSubscriptionPolicy) {
    amplitudeInfo.routineInfo = { 
      routineShippingId : goodsInfo.goodsBasicInfo?.goodsSubscriptionPolicy ? goodsInfo.goodsBasicInfo?.goodsSubscriptionPolicy?.subscriptionPolicySeq : null,
      routineShippingName : goodsInfo.goodsBasicInfo?.goodsSubscriptionPolicy ? goodsInfo.goodsBasicInfo?.goodsSubscriptionPolicy?.userPolicyName : null,
      routineShippingPeriod : goodsInfo.goodsBasicInfo?.goodsSubscriptionPolicy ? goodsInfo.goodsBasicInfo?.goodsSubscriptionPolicy?.allRoundCount : null
    }
  }

   //Amplitude Script
   const eventProperties :Record<string, any> = {   
    source: router.options.history.state.back,
    product_id: amplitudeInfo.id,
    product_name: amplitudeInfo.name,
    product_code: amplitudeInfo.code,
    product_type: amplitudeInfo.type,
    brand_name: amplitudeInfo.brandName,
    discount_rate: amplitudeInfo.discountRate + '%',
    price: amplitudeInfo.price,
    discount_price: amplitudeInfo.discountPrice,
    review_count: amplitudeInfo.reviewCount,
  }

  if (amplitudeInfo?.routineInfo) {
    eventProperties.routine_shipping_id = amplitudeInfo?.routineInfo?.routineShippingId;
    eventProperties.routine_shipping_name = amplitudeInfo?.routineInfo?.routineShippingName;
    eventProperties.routine_shipping_period = amplitudeInfo?.routineInfo?.routineShippingPeriod;
  }

  amplitudeSendEvent('view_product_page', eventProperties);

  //flareLane Script
  const flareLaneEventProperties = {
    product_id: amplitudeInfo.id,
    product_name: amplitudeInfo.name,
    product_type: amplitudeInfo.type === 'SUBSCRIPTION' ? '정기구독' : '단품',
    routine_shipping_name: amplitudeInfo.routineShippingName,
    routine_shipping_period: amplitudeInfo.routineShippingPeriod,
    brand_name: amplitudeInfo.brandName,
  }

  await flareLaneTrackEvent('view_product_page', flareLaneEventProperties);

  const flareLaneUser = {
    last_view_product: amplitudeInfo.name,
    last_view_productid: amplitudeInfo.id
  }

  await flareLaneSetTag(flareLaneUser);

  await flareLaneDisplayInApp("product_page")

  return adInfo
};


export const setModuleInfo = (
  isInit: boolean,
  items?: Record<string, any>[],
  totalGoodsCnt?: number,
  totalPrice?: number
) => {
  const moduleItem: Record<string, any> = {};
  let itemList: any[] = [];

  if (!items || items.length === 0) {
    const item: Record<string, any> = {
      item_id: goodsInfo.goodsBasicInfo?.goodsSeq,
      item_name: goodsInfo.goodsBasicInfo?.goodsName,
      item_brand: goodsInfo.goodsBasicInfo?.brandGroup ?? '',
      item_variant: goodsInfo.goodsBasicInfo?.defaultOption?.optionItemGroups?.[0]?.optionName ?? '',
      price: goodsInfo.goodsBasicInfo?.regularPrice,
      value: goodsInfo.goodsBasicInfo?.salePrice,
      quantity: isInit ? 1 : totalGoodsCnt ?? 1,
    };
    itemList.push(item);
  } else {
    itemList = items;
  }

  if (isInit) {
    analyticeEventSimple('view_item', {
      enter_url: document.referrer,
      currency: 'KRW',
      value: totalPrice || goodsInfo.goodsBasicInfo.regularPrice,
      items: itemList,
    });
  }

  moduleItem.items = itemList;
  
  return {
    id: goodsInfo.goodsBasicInfo?.goodsSeq,
    price: goodsInfo.goodsBasicInfo?.regularPrice,
    moduleItem,
  };
};


/**
 * 상품상세 - 네이버 구매 클릭 이벤트
 * @param naverData 
 * @param transactionId 
 */
export const adForNaver = (naverData: Record<string, any>, transactionId: string) => {

  facebookPixelProductEvent('trackCustom', 'Purchase_NPay', {
    content_ids: naverData.items[0].item_id,
    content_type: 'product',
    value: naverData.price,
    salePrice: naverData.price,
    currency: 'KRW',
    content_name: naverData.items[0].item_name
  })

  let semanticAttributes = {
    transactionID: transactionId,
    totalValue: naverData.price,
    products: [{
      productID: naverData.items[0].item_id
    }]
  }

  airbridgeSendEvent('Click_Npay', naverData.items[0].item_name, 'MW_', naverData.price, semanticAttributes)

}

/**
 * 상품상세 - 또박이동시 마케팅
 * @param quantity 
 */
export const makeModuleItemGoTtobak = (quantity: number) => {
  const eventProperties = {
    product_id: amplitudeInfo.id, // 상품코드
    product_name: amplitudeInfo.name, // 상품명
    product_code: amplitudeInfo.code, // 품목코드
    product_quantity: quantity, // 제품수량
    brand_name: amplitudeInfo.brandName, // 브랜드명
    price: amplitudeInfo.price, // 판매가
    review_count: amplitudeInfo.reviewCount, // 리뷰갯수
  };
  amplitudeSendEvent('click_routine_shipping_button', eventProperties);
}
/**
 * 상품상세 - 선물하기 클릭시 
 * @param quantity 
 */
export const makeModuleItemGiftClick = (quantity: number) => {
  const eventProperties = {
    product_id: amplitudeInfo.id,
    product_name: amplitudeInfo.name,
    product_code: amplitudeInfo.code,
    product_quantity: quantity,
    brand_name: amplitudeInfo.brandName,
    discount_rate: amplitudeInfo.discountRate + '%',
    price: amplitudeInfo.price,
    discount_price: amplitudeInfo.discountPrice,
    review_count: amplitudeInfo.reviewCount,
  };
  amplitudeSendEvent('click_gift_button', eventProperties);
}

/**
 * 장바구니 / 바로구매 클릭 이벤트시 마케팅정보
 * @param mode 장바구니 , 바로구매
 * @param quantity 개수
 * @param optionName 옵션명
 */
export const makeModuleItemClickOrder = (mode: string, quantity: number, optionName: string) => {
  if (mode === "CART") {
    // 장바구니 클릭 시 앰플리튜드 이벤트 전송
    const eventProperties = {
      product_id: amplitudeInfo.id, // 상품코드
      product_name: amplitudeInfo.name, // 상품명
      product_type: amplitudeInfo.type === 'SUBSCRIPTION' ? '정기구독' : '단품',
      product_code: amplitudeInfo.code, // 품목코드
      product_quantity: quantity, // 제품수량
      brand_name: amplitudeInfo.brandName, // 브랜드명
      discount_rate: amplitudeInfo.discountRate + '%', // 할인률
      price: amplitudeInfo.price, // 판매가
      discount_price: amplitudeInfo.discountPrice, // 할인가
      review_count: amplitudeInfo.reviewCount, // 리뷰갯수
      review_point: amplitudeInfo.reviewPoint, // 리뷰점수
      product_option: optionName// 상품의 옵션
    };
    amplitudeSendEvent('add_to_cart', eventProperties);

    const flareLaneEventProperties = {
      product_id: amplitudeInfo.id, // 상품코드
      product_name: amplitudeInfo.name, // 상품명
      product_type: amplitudeInfo.type === 'SUBSCRIPTION' ? '정기구독' : '단품',
      product_quantity: quantity, // 제품수량
      product_option: optionName, // 상품의 옵션
      brand_name: amplitudeInfo.brandName, // 브랜드명
    };
    flareLaneTrackEvent('add_to_cart', flareLaneEventProperties);

  } else if (mode === "DIRECT") {
    // 바로구매 클릭 시 앰플리튜드 이벤트 전송
    const eventProperties = {
      product_id: amplitudeInfo.id, // 상품코드
      product_name: amplitudeInfo.name, // 상품명
      product_code: amplitudeInfo.code, // 품목코드
      product_type: amplitudeInfo.type === 'SUBSCRIPTION' ? '정기구독' : '단품',
      routine_shipping_id: amplitudeInfo.routineShippingId,
      routine_shipping_name: amplitudeInfo.routineShippingName,
      routine_shipping_period: amplitudeInfo.routineShippingPeriod,
      product_quantity: quantity, // 제품수량
      brand_name: amplitudeInfo.brandName, // 브랜드명
      discount_rate: amplitudeInfo.discountRate + '%', // 할인률
      price: amplitudeInfo.price, // 판매가
      discount_price: amplitudeInfo.discountPrice, // 할인가
      review_count: amplitudeInfo.reviewCount, // 리뷰갯수
      review_point: amplitudeInfo.reviewPoint, // 리뷰점수
      product_option: optionName  // 상품의 옵션
    };
    amplitudeSendEvent('click_order_now_button', eventProperties);
  }
}

/**
 * 결제페이지 진입 마케팅정보
 * @param mode 01 : 단일상품 , 02 : 정기구독 , 03 : 결합
 * @param orderList 
 * @param realPaymentPrice 
 */
export const makeModuleItemsPayInit = async (mode: string , orderList: Record<string, any>[], realPaymentPrice: number): Promise<Record<string, any>> => {
  const router = useRouter();
  const adInfo: Record<string, any> = {}

  const facebookItems = orderList.map((item) => {
    return { id: item.goodsSeq, quantity: item.quantity }
  })

  adInfo.facebookInfo = {}
  adInfo.facebookInfo.facebookItems = facebookItems
  adInfo.facebookInfo.salePrice = realPaymentPrice
  adInfo.facebookInfo.goodsName = orderList.length === 1 ? orderList[0].goodsName : `${orderList[0].goodsName} 외 ${orderList.length - 1}개`

  adInfo.googleInfo = {}
  orderList.forEach((item) => {
    const googleItem = {
      item_id: item.goodsSeq,
      item_name: item.goodsName,
      item_variant: item.optionNames ? item.optionNames[0].name : '',
      price: item.price,
      value: item.salePrice,
      quantity: item.quantity
    }
    adInfo.googleInfo.googleItems = []
    adInfo.googleInfo.googleItems.push(googleItem)
  })
  adInfo.googleInfo.totalPrice = realPaymentPrice

  //상품이 한개인지 여러개인지 체크
  const isSeveral = orderList?.length > 1 ? true : false;
  //상품의 타입
  const getProductType = (item: any) =>  item?.goodsName.includes('체험팩') ? '체험팩' : (item?.goodsType === 'NORMAL' ? '단품' : '정기구독');
  //값이 있는지 체크
  const isCheckValue = <T>(list: any[], extractor: (item: any) => T): T[] => {
    return list.map(extractor).filter(v => v !== null && v !== undefined);
  };

  //Amplitude Script
  const eventProperties : Record<string, any> = {
    source: router?.options?.history?.state?.back,
    product_id: isSeveral ? orderList.map(item => item.goodsSeq) : orderList[0]?.goodsSeq,
    product_name: isSeveral ? orderList.map(item => item.goodsName) : orderList[0]?.goodsName,
    product_quantity: orderList.reduce((total, item) => total + item.quantity, 0),
    product_type: isSeveral ? orderList.map(item => getProductType(item)) : getProductType(orderList[0]),
    product_code: orderList.map(item => item.erpSyncCode),
    brand_name: orderList.map(item => item.brandGroup),
    total_order_amount: orderList.reduce((total, item) => total + item.totalSellPrice, 0),
  };

  const flareLaneEventProperties : Record<string, any> = {
    product_id: isSeveral ? orderList.map(item => item.goodsSeq) : orderList[0]?.goodsSeq,
    product_name: isSeveral ? orderList.map(item => item.goodsName) : orderList[0]?.goodsName,
    product_quantity: orderList.reduce((total, item) => total + item.quantity, 0),
    product_type: isSeveral ? orderList.map(item => getProductType(item)) : getProductType(orderList[0]),
    brand_name: orderList.map(item => item.brandGroup),
  }

  //1. 값이 있다면 추가할 것
  const optionnalProperties : Record<string, any> = {
    product_option: isCheckValue(orderList, item => item.optionName),
    routine_shipping_id: isCheckValue(orderList, item => item.goodsSubscriptionPolicy?.subscriptionPolicySeq),
    routine_shipping_name: isCheckValue(orderList, item => item.goodsSubscriptionPolicy?.userPolicyName),
    routine_shipping_period: isCheckValue(orderList, item => item.goodsSubscriptionPolicy?.allRoundCount),
  }

  //2. 빈 배열이 아닌 경우에만 필드 추가
  Object.entries(optionnalProperties).forEach(([key, value]) => {
    if (value.length > 0) {
      eventProperties[key] = value;
    }
  });

  if (optionnalProperties.product_option.length > 0) {
    flareLaneEventProperties.product_option = optionnalProperties.product_option;
  }

  flareLaneTrackEvent('view_order_form', flareLaneEventProperties);
  amplitudeSendEvent('view_order_form', eventProperties);

  return adInfo

}

/**
 * 결제화면 결제하기 클릭시 마케팅정보
 * @param params 결제 param
 * @param orderList 주문리스트
 * @param showUseCouponList 사용쿠폰리스트
 */
export const makeModuleItemsPayClick = async (orderList: Record<string, any>[], showUseCouponList: Record<string, any>[], paymentInfo: Record<string, any>) => {

  //상품이 한개인지 여러개인지 체크
  const isSeveral = orderList?.length > 1 ? true : false;
  //상품의 타입
  const getProductType = (item: any) =>  item?.goodsName.includes('체험팩') ? '체험팩' : (item?.goodsType === 'NORMAL' ? '단품' : '정기구독');
  
  const memberStore = useMemberStore();
  const couponSeq = showUseCouponList.map(coupon => coupon ? coupon.cartOptionSeq : '')
  const couponNames = showUseCouponList.map(coupon => coupon ? coupon.couponName : '')
  const couponTypes = showUseCouponList.map(coupon => coupon ? coupon.saleType : '')
  const couponRates = showUseCouponList.map(coupon => coupon ? coupon.percentGoodsSale : '')
  const couponPrice = showUseCouponList.map(coupon => coupon ? coupon.couponSaleAmount : '')

  const eventProperties : Record<string, any> = {
    product_id: isSeveral ? orderList.map(item => item.goodsSeq) : orderList[0]?.goodsSeq,
    product_name: isSeveral ? orderList.map(item => item.goodsName) : orderList[0]?.goodsName,
    product_quantity: paymentInfo.totalQuantity,
    product_type: isSeveral ? orderList.map(item => getProductType(item)) : getProductType(orderList[0]),
    product_code: orderList.map(item => item.erpSyncCode),
    brand_name: orderList.map(item => item.brandGroup),
    coupon_id: couponSeq,
    coupon_type: couponTypes.every(type => type === 'percent') ? '정률쿠폰' : '정액쿠폰',
    coupon_name: couponNames,
    coupon_discount_rate: couponRates,
    coupon_discount_price: couponPrice.reduce((accumulator, current) => accumulator + current, 0),
    level_discount: orderList[0].groupSalePercent,
    saving_point: memberStore.emoney,
    using_point: paymentInfo.emoney,
    total_order_amount: paymentInfo.goodsOriSumPrice,
    total_discount_amount: paymentInfo.totalSumSalePrice,
    total_payment_amount: paymentInfo.realPaymentPrice,
    payment_type: paymentInfo.paymentType,
    shipping_charge: paymentInfo.deliveryPrice,
    routine_shipping_date: paymentInfo.paymentDay ? paymentInfo.paymentDay : '',
    product_option: orderList.map(item => item.optionName).filter(id => id !== null && id !== undefined),
  };

  amplitudeSendEvent('click_to_pay_button', eventProperties);

  // 페이스북 마케팅
  const facebookItems = orderList.map((item) => {
    return { id: item.goodsSeq, quantity: item.quantity }
  });

  // 플레어레인 setTag
  const flareLaneUser = {
    coupon_Chk : showUseCouponList?.length > 0 ? 'Y' : 'N',
    coupon_name : couponNames,
    coupon_seq : couponSeq,
  };

  await flareLaneSetTag(flareLaneUser);

  const goodsName = orderList.length === 1 ? orderList[0].goodsName : `${orderList[0].goodsName} 외 ${orderList.length - 1}개`
  facebookPixelProductEvent('track', 'AddPaymentInfo', { salePrice: paymentInfo.realPaymentPrice, goodsName: goodsName }, facebookItems)
  googleAdsEvent({
    send_to: `${googleADSThreeKey()}/Fi4jCLKM2ckYEKHlhukp`,
    value: 1.0,
    currency: 'KRW',
    transaction_id: '',
    event_callback: gtag_report_conversion()
  })

}



/**
 * 장바구니 진입시 마케팅정보 
 * @param carts 장바구니정보
 * @param totalPrice 총가격
 */
export const makeModuleItemsCartInit = (carts: Record<string, any>[], totalPrice: number): Record<string, any> => {
  const adInfo: Record<string, any> = {
    googleData: {},
    mobionData: {},
    criteoData: {}
  }
  carts.forEach(item => {
    const googleItem = {
      item_id: item.goodsSeq,
      item_name: item.goodsName,
      item_variant: item.optionNames ?? '',
      price: item.regularPrice,
      value: item.salePrice,
      quantity: item.quantity
    }
    const criteoItem = {
      id: item.goodsSeq,
      price: item.salePrice,
      quantity: item.quantity
    }
    const mobionItem = {
      productCode: item.goodsSeq,
      productName: item.goodsName,
      price: item.regularPrice,
      dcPrice: item.salePrice,
      qty: item.quantity
    }
    adInfo.googleData.googleItems = []
    adInfo.criteoData.criteoItems = []
    adInfo.mobionData.mobionItems = []
    adInfo.googleData.googleItems.push(googleItem) // 구글
    adInfo.criteoData.criteoItems.push(criteoItem) // 크리테오
    adInfo.mobionData.mobionItems.push(mobionItem)
  })
  adInfo.googleData.totalPrice = totalPrice
  adInfo.mobionData.totalPrice = totalPrice
  adInfo.mobionData.mobionItems = JSON.stringify(adInfo.mobionData.mobionItems)
  adInfo.mobionData.totalQty = carts.reduce((sum, item) => { return sum + item.quantity }, 0)

  return adInfo
};

/**
 * 장바구니리스트에서 주문하기 버튼 클릭시
 * @param cartList 
 * @param deliveryPrice 
 */
export const makeModuleItemsOrderClick = (mode: string, cartList: Record<string, any>[], deliveryPrice: number) => {
  //상품이 한개인지 여러개인지 체크
  const isSeveral = cartList?.length > 1 ? true : false;
  //상품의 타입
  const getProductType = (item: any) =>  item?.goodsName.includes('체험팩') ? '체험팩' : (item?.goodsType === 'NORMAL' ? '단품' : '정기구독');
  const eventProperties = {
    order_type: mode == '01' ? '전체주문' : '선택주문',
    total_cart_count: cartList.length,
    total_order_amount: cartList.reduce((sum, item) => { return sum + item.totalRegularPrice }, 0),
    total_discount_amount: cartList.reduce((sum, item) => { return sum + (item.totalRegularPrice - item.totalSalePrice) }, 0),
    total_payment_amount: cartList.reduce((sum, item) => { return sum + item.totalSalePrice }, 0),
    shipping_charge: deliveryPrice,
    product_id: isSeveral ? cartList.map(item => item.goodsSeq) : cartList[0]?.goodsSeq,
    product_name: isSeveral ? cartList.map(item => item.goodsName) : cartList[0]?.goodsName,
    product_quantity: cartList.reduce((total, item) => total + item.quantity, 0),
    product_type: isSeveral ? cartList.map(item => getProductType(item)) : getProductType(cartList[0]),
    product_code: cartList.map(item => item.nutrientAlarmCode || ''),
    brand_name: cartList.map(item => item.brandGroup),
    discount_rate : isSeveral ? cartList.map(item => item.salePercent + '%') : cartList[0]?.salePercent + '%',
    price : isSeveral ? cartList.map(item => item.salePrice) : cartList[0]?.salePrice,
  }
  amplitudeSendEvent('click_cart_order_button', eventProperties);
}


/**
 * 결제완료시
 * @param orderInfo 
 * @param ordNo 
 * @param isGift 
 */
export const makeModuleItemsOrderComplete = async (
  orderInfo: Record<string, any>, 
  ordNo: string, 
  isGift: boolean
): Promise<Record<string, any>> => {
  const memberStore = useMemberStore()
  const amplitudeDate = toFormatDate(new Date(), "YYYY-MM-DD")

  const adInfo: Record<string, any> = {
    airBridgeItems: [],
    amplitudeItems: [],
    googleItems: [],
    mobionItems: [],
    criteoItems: [],
    facebookItems: [],
    kakaoItmes: [],
  }

  const orderCompleteInfoList = orderInfo.orderCompleteInfoList

  const allOrderOptions = orderCompleteInfoList.flatMap((item : any) => item.orderOptionList)

  adInfo.goodsName =
    orderCompleteInfoList.length === 1
      ? orderCompleteInfoList[0].goodsName
      : `${orderCompleteInfoList[0].goodsName} 외 ${orderCompleteInfoList.length - 1}개`
  adInfo.totalPrice = orderInfo.paymentPrice
  adInfo.userName = memberStore.memberName
  adInfo.ordNo = ordNo
  adInfo.brandName =
    orderCompleteInfoList.length === 1 ? orderCompleteInfoList[0].orderOptionList[0].brandGroup : ''
  adInfo.deliveryPrice = orderCompleteInfoList[0].totalShippingPrice
  adInfo.totalEa = orderCompleteInfoList.reduce(
    (total: number, item: Record<string, any>) => total + (item.itemTotalQuantity || 0),
    0
  )
  adInfo.isTtobak = orderInfo?.isContainSubscriptionGoods ? '또박' : '단품'
  adInfo.payment = orderCompleteInfoList[0]?.paymentType
  adInfo.isFirstOrder = orderInfo.marketingDataOnOrderComplete?.isFirstOrder

  orderCompleteInfoList.forEach((orderItem : any) => {
    orderItem.orderOptionList.forEach(async (item : any, index : any) => {
      const hasSubscription = allOrderOptions.some((goods : any) => goods.goodsType === 'SUBSCRIPTION')
      const hasSingle = allOrderOptions.some((goods : any) => goods.goodsType === 'NORMAL')
      const productType = hasSubscription && hasSingle ? '단품+정기구독' : hasSingle ? '단품': item.isExperiencePack ? '체험팩' : hasSubscription ? '정기구독' : item.productType

      adInfo.airBridgeItems.push({
        productID: item.goodsSeq,
        name: item.goodsName,
        price: item.sellPrice,
        currency: 'KRW',
        quantity: item.quantity,
        position: adInfo.airBridgeItems.length
      })

      adInfo.googleItems.push({
        item_id: item.goodsSeq,
        item_name: item.goodsName,
        item_brand: item.goodsKind,
        item_variant: '',
        price: item.price,
        value: item.sellPrice,
        quantity: item.quantity
      })

      adInfo.mobionItems.push({
        productCode: item.goodsSeq,
        productName: item.goodsName,
        qty: item.quantity,
        price: item.price,
        dcPrice: item.sellPrice,
      })

      adInfo.criteoItems.push({
        id: item.goodsSeq,
        price: item.sellPrice,
        quantity: item.quantity
      })

      adInfo.facebookItems.push({
        id: item.goodsSeq,
        quantity: item.quantity
      })

      adInfo.kakaoItmes.push({
        id: String(item.goodsSeq),
        name: item.goodsName,
        quantity: String(item.quantity),
        price: String(item.sellPrice),
      })

      const amplitude: Record<string, any> = {
        price: item.price,
        product_quantity: item.quantity,
        product_revenue: item.quantity > 1 ? Math.floor(item.sellPrice / item.quantity) : item.sellPrice,
        product_id: item.goodsSeq,
        product_name: item.goodsName,
        product_type: item.goodsType === 'GIFT' ? '증정품' : item.isExperiencePack ? '체험팩' : orderItem.orderType === "SUBSCRIPTION" ? "정기구독" : '단품',
        product_code: item.nutrientAlarmCode || '',
        transactionid: orderItem.orderSeq,
        brand_name: item.brandGroup,
        discount_rate: item.salePercent + '%',
        discount_price: item.sellPrice,
        review_count: item.reviewCount,
        level_discount: item.memberDiscountPrice,
        saving_point: memberStore.emoney,
        using_point: orderItem.emoneyDiscountPrice || 0,
        payment_type: orderItem.paymentType,
        shipping_charge: orderItem.totalShippingPrice || 0,
        agree_expert_consultation: 'N',
        product_option: item.optionNameList?.length > 0 ? item.optionNameList[0] : item.optionName,
        purchase_method: isGift ? 'gift' : 'self',
        subOrderCode: ordNo,
        total_discount_amount:
          (orderItem.emoneyDiscountPrice || 0) +
          (orderItem.couponDiscountPrice || 0) +
          (orderItem.eventDiscountPrice || 0) +
          (orderItem.memberDiscountPrice || 0),
        subProduct_type: productType,
      }

      //옵션list에 2개 이상 있다면 처리
      if (item.optionNameList?.length > 1) {
        amplitude.product_option2 = item.optionNameList[1];
      }

      if (orderItem.orderType === "SUBSCRIPTION") {
        amplitude.routine_shipping_id = orderItem.goodsSubscriptionPolicy?.subscriptionPolicySeq
        amplitude.routine_shipping_name = orderItem.goodsSubscriptionPolicy?.userPolicyName
        amplitude.routine_shipping_period = orderItem.goodsSubscriptionPolicy?.allRoundCount
        amplitude.routine_shipping_date = orderItem.fixedPaymentDay
      }

      if (item.usedCouponInfoList?.length > 0) {
        amplitude.coupon_id = item.usedCouponInfoList.map((c: any) => c.couponSeq)
        amplitude.coupon_type = item.usedCouponInfoList.map((c: any) =>
          c.discountType === 'RATE' ? '정률쿠폰' : '정액쿠폰'
        )
        amplitude.coupon_name = item.usedCouponInfoList.map((c: any) => c.couponName)
        amplitude.coupon_discount_rate = item.usedCouponInfoList.map((c: any) => c.discountRate)
        amplitude.coupon_discount_price = item.usedCouponInfoList.map((c: any) => c.discountPrice)
      }

      adInfo.amplitudeItems.push(amplitude);

      const marketingOnly = orderInfo.marketingDataOnOrderComplete
      amplitudeSendEvent('complete_purchase', amplitude, {
        last_purchase_date: amplitudeDate,
        gift_count: 0,
        purchase_trial: item.isExperiencePack ? 'Y' : 'N',
        purchase_routine_shipping: hasSubscription ? 'Y' : 'N',
        purchase_single: productType === 'SINGLE' ? 'Y' : 'N',
      })

      await flareLaneTrackEvent('complete_purchase', {
        product_id: item.goodsSeq,
        product_price: item.price,
        product_quantity: item.quantity,
        product_revenue: item.sellPrice,
        product_name: item.goodsName,
        product_type: productType,
        product_option: item.optionName,
        brand_name: item.categoryTitle,
        routine_shipping_name: orderItem.goodsSubscriptionPolicy?.userPolicyName,
        routine_shipping_period: orderItem.goodsSubscriptionPolicy?.allRoundCount,
        routine_shipping_date: orderItem.goodsSubscriptionPolicy?.routinePaymentDay,
        transactionid: ordNo,
      })

      await flareLaneSetTag({
        membership_level: memberStore.memberGroupName,
        saving_point: memberStore.emoney,
        total_order_amount: marketingOnly?.totalOrderPrice,
        single_order_num: marketingOnly?.normalGoodsOrderCnt,
        routine_shipping_order_num : marketingOnly?.subscriptionGoodsOrderCnt,
        total_order_num: (marketingOnly?.normalGoodsOrderCnt || 0) + (marketingOnly?.subscriptionGoodsOrderCnt || 0),
        last_purchase_date: marketingOnly?.lastNormalGoodsOrderDate,
        last_subscribe_date: marketingOnly?.lastSubscriptionGoodsOrderDate,
        last_purchase_product: marketingOnly?.lastNormalOrderGoodsName,
        last_subscribe_product: marketingOnly?.lastSubscriptionOrderGoodsName,
        subscribe_status: marketingOnly?.isSubscribeUser === 'Y' ? 'Y' : 'N',
        start_date_subscribe: marketingOnly?.firstSubscriptionDate,
      })
    })
  })

  return adInfo
};

/**
 * 결제취소시
 * @param orderCancleInfo  //취소할주문정보
 * @param cancleUserInfo //사용자마케팅정보
 * @param cancleReason //취소이유
 */

export const makeMoudleItemOrderCancle = async ( orderCancleInfo : any, cancleUserInfo : any, cancleReason : string) => {
  //1.props.orderGroupCancelList orderList를 돌면서 orderOptionList를 돌면서 subscriptionPolicySeq가 빈값이 아니라면 정기구독으로 보내야함.
  //2.props.orderGroupCancelList orderList를 돌면서 orderOptionList를 돌면서 subscriptionPolicySeq가 빈값이면 단품으로 보내야함.
  //3.props.orderGroupCancelList orderList를 돌면서 orderOptionList를 돌면서 단품 상품과, 정기구독 상품을 따로 뺀다
  const orderSingleGoods: any[] = [];
  const orderSubscribeGoods: any[] = [];

  orderCancleInfo?.orderList?.forEach((item: any)=>{
    if (item.subscriptionOrderSeq !== null && item.orderType === 'Subscription') {
      orderSubscribeGoods.push(item);
    } else {
      orderSingleGoods.push(item);
    }
  });

  console.log('orderSingleGoods:::::::', orderSingleGoods);
  console.log('orderSubscribeGoods:::::::', orderSubscribeGoods);

  //단품용 이벤트프로퍼티
  const singleEventProperties = {
    product_id: orderSingleGoods?.flatMap(order => order?.orderOptionList?.map((item: any) => item.goodsSeq)),
    product_name: orderSingleGoods?.flatMap(order => order?.orderOptionList?.map((item: any) => item.goodsName)),
    product_code: orderSingleGoods?.flatMap(order => order?.orderOptionList?.map((item: any) => item.nutrientAlarmCode)),
    brand_name: orderSingleGoods?.flatMap(order => order?.orderOptionList?.map((item: any) => item.brandGroup))
  };

  //정기구독용 이벤트프로퍼티
  const subscribeEventPropertiesList = {
    cancel_reason: cancleReason,
    product_id: orderSubscribeGoods?.flatMap(order => order?.orderOptionList?.map((item: any) => item.goodsSeq)),
    product_name: orderSubscribeGoods?.flatMap(order => order?.orderOptionList?.map((item: any) => item.goodsName)),
    product_code: orderSubscribeGoods?.flatMap(order => order?.orderOptionList?.map((item: any) => item.nutrientAlarmCode)),
    brand_name: orderSubscribeGoods?.flatMap(order => order?.orderOptionList?.map((item: any) => item.brandGroup)),
    routine_shipping_id: orderSubscribeGoods?.flatMap(order => 
      order?.orderOptionList?.map((item: any) => item.subscriptionPolicySeq)
    ).filter(id => id !== null && id !== undefined), //구독했던 또박배송 정책 고유번호
    routine_shipping_name: orderSubscribeGoods?.flatMap(order => 
      order?.orderOptionList?.map((item: any) => item.subscriptionPolicyName)
    ).filter(id => id !== null && id !== undefined), //구독했던 또박배송 정책명
    routine_shipping_payment_times: orderSubscribeGoods?.flatMap(order => order?.orderOptionList?.map((item: any) => item.paymentTimes)), //해지한 상품의 정기구독 내 결제 회차
    routine_shipping_date: orderSubscribeGoods?.map(order => order?.paymentDate), //해지한 상품의 정기구독 신청 일
    routine_shipping_cancel_date: dayjs(new Date()).format('YYYY-MM-DD') //해지한 상품의 정기구독 해지 일 = 오늘
  };

  //단품용 유저프로퍼티
  const singleUserProperties = {
    total_order_num : cancleUserInfo?.totalOrderCnt,
    single_order_num : cancleUserInfo?.totalSingleOrderCnt
  };

  //정기구독용 유저프로퍼티
  const subScibeProperties = {
    routine_shipping_order_num : cancleUserInfo?.subscriptionOrderCnt, //현재 구독 유지중인 정기구독 건수
    routine_shipping_payment_times : cancleUserInfo?.subscriptionMaxPaymentTimes, //총 정기구독 결제 회차(누적)
    subscribe_status : cancleUserInfo?.subscriptionOrderMember ? 'Y' : 'N', //현재 구독중인 상품 여부
    last_purchase_routine_shipping_date : cancleUserInfo?.lastSubscriptionOrderPaymentDate, //마지막 정기구독 주문날짜
    total_order_num : cancleUserInfo?.totalOrderCnt,
  };

  //airbridge 단품일때
  let semanticAttributes = {
    products: orderSingleGoods?.flatMap((item: any) =>
      item.orderOptionList?.map((option: any) => ({
        name: option.goodsName,  // 상품명
        productID: option.goodsSeq, // 상품 ID
        price: option.sellPrice, // 가격
        quantity: item.itemTotalQuantity, // 수량
        currency: 'KRW',
      })) || []
    ),
    totalValue: orderSingleGoods?.reduce((acc: number, item: any) => acc + item.paymentPrice, 0),
    currency: 'KRW',
    transactionID: orderSingleGoods?.map((item: any) => item.orderSeq),
  };

  //airbridge 정기구독상품일때
  let subScribeSemanticAttributes = {
    products: orderSubscribeGoods?.flatMap((item: any) =>
      item.orderOptionList?.map((option: any) => ({
        name: option.goodsName,
        productID: option.goodsSeq,
        price: option.sellPrice,
        quantity: item.itemTotalQuantity,
        currency: 'KRW',
      })) || []
    ),
    totalValue: orderSubscribeGoods?.reduce((acc: number, item: any) => acc + item.paymentPrice, 0),
    currency: 'KRW',
    transactionID: orderSubscribeGoods?.map((item: any) => item.orderSeq),
  };

  //airbridge 단품용 데이터
  let airbridgeSingleBrandName = orderSingleGoods[0]?.orderOptionList[0]?.brandGroup;
  let airbridgeSinglePrice = orderSingleGoods?.reduce((acc: number, item: any) => acc + item.paymentPrice, 0);
  let airbridgeSingleGoodsName = orderSingleGoods?.flatMap(order => 
      order?.orderOptionList?.map((item: any) => item.goodsName)
  )?.reduce((acc, item, index, arr) => 
      index === 0 ? (arr.length > 1 ? `${item} 외 ${arr.length - 1}건` : item) : acc, ""
  ) ?? "";

  //airbridge 정기구독용 데이터
  let airbridgeSubscribeBrandName = orderSingleGoods[0]?.orderOptionList[0]?.brandGroup;
  let airbridgeSubscribePrice = orderSingleGoods?.reduce((acc: number, item: any) => acc + item.paymentPrice, 0);
  let airbridgeSubscribeGoodsName = orderSingleGoods?.flatMap(order => 
      order?.orderOptionList?.map((item: any) => item.goodsName)
  )?.reduce((acc, item, index, arr) => 
      index === 0 ? (arr.length > 1 ? `${item} 외 ${arr.length - 1}건` : item) : acc, ""
  ) ?? "";


  //단품상품이 있는경우
  if (orderSingleGoods?.length > 0) {
    amplitudeSendEvent('refunded_purchase', singleEventProperties, singleUserProperties);
    await flareLaneTrackEvent('refunded_purchase', singleEventProperties);
    await flareLaneSetTag(singleUserProperties);
    airbridgeSendEvent('airbridge.ecommerce.order.canceled', airbridgeSingleBrandName, airbridgeSingleGoodsName, airbridgeSinglePrice, semanticAttributes);
  };

  //정기구독 상품이 있는경우
  if (orderSubscribeGoods?.length > 0) {
    amplitudeSendEvent('cancel_subscribe', subscribeEventPropertiesList, subScibeProperties);
    await flareLaneTrackEvent('cancel_subscribe', subscribeEventPropertiesList);
    await flareLaneSetTag(subScibeProperties);
    airbridgeSendEvent('airbridge.unsubscribe', airbridgeSubscribeGoodsName, airbridgeSubscribeBrandName, airbridgeSubscribePrice, subScribeSemanticAttributes);
  };
}


/**
 * 정기구독 해지 완료시
 * @param cancleTtobakInfo  //해지할 또박정보
 * @param cancleUserInfo //사용자마케팅정보
 * @param cancleReason //취소이유
 * @param ttobakId //또박번호
 */

export const makeMoudleItemSubScribeOrderCancle = async ( cancleTtobakInfo : any, cancleUserInfo : any, cancleReason : any, cancleDetailReason : any, ttobakId : any) => {

  console.log('cancleTtobakInfo::::::', cancleTtobakInfo);
  console.log('cancleUserInfo::::::', cancleUserInfo);
  console.log('cancleReason::::::', cancleReason);
  console.log('ttobakId::::::', ttobakId);

  const eventProperties = {
    cancel_reason : cancleReason, //구독 취소 유형
    cancel_detail_reason : cancleDetailReason, //구독 취소 상세 유형
    product_id : cancleTtobakInfo?.goodsSeq, //상품코드
    product_name : cancleTtobakInfo?.name, //상품명
    product_code : cancleTtobakInfo?.productCode, //품목코드
    routine_shipping_id : cancleTtobakInfo?.infoSeq, //구독했던 또박배송 정책 고유번호
    routine_shipping_name : cancleTtobakInfo?.infoName, //구독했던 또박배송 정책명
    routine_shipping_payment_times : cancleTtobakInfo?.nowRound, //해지한 상품의 정기구독 결제 회차
    routine_shipping_date : cancleTtobakInfo?.regDate, //해지한 상품의 정기구독 신청 일
    routine_shipping_cancel_date : dayjs(new Date()).format('YYYY-MM-DD'), //해지한 상품의 정기구독 해지 일 = 오늘
    brand_name : cancleTtobakInfo?.brandName, //브랜드명
  }

  const userProperties = {
    routine_shipping_order_num : cancleUserInfo?.routineShippingOrderNum, //현재 구독 유지중인 정기구독건수
    routine_shipping_payment_times : cancleUserInfo?.routineShippingPaymentTimes, //총 정기구독 결제 회차
    subscribe_status : cancleUserInfo?.purchaseRoutineShipping, //현재 구독중인 상품이 있는지의 여부
    last_purchase_routine_shipping_date : cancleUserInfo?.lastPurchaseRoutineShippingDate, //마지막 정기구독 주문 날짜
    total_order_num : cancleUserInfo?.totalOrderNum //총 주문 횟수 [단품 결제 횟수+총 정기구독 결제 회차 (누적)]
  }

  //에어브릿지 semanticAttributes
  let semanticAttributes = {
    products: [{
      productID: cancleTtobakInfo?.goodsSeq, //상품번호
      name: cancleTtobakInfo?.name, //상품이름
      price: cancleTtobakInfo?.originalPrice, //상품원가
      currency: 'KRW',
    }],
    totalValue: cancleTtobakInfo?.sellPrice, //결제금액
    currency: 'KRW',
    transactionID: ttobakId, //주문번호
  }

  amplitudeSendEvent('cancel_subscribe', eventProperties, userProperties);
  await flareLaneTrackEvent('cancel_subscribe', eventProperties);
  await flareLaneSetTag(userProperties);
  airbridgeSendEvent('airbridge.unsubscribe', cancleTtobakInfo?.name, cancleTtobakInfo?.brandName, cancleUserInfo?.sellPrice, semanticAttributes);
}


/**
 * 로그인 이벤트 처리
 * @param data 사용 인증 데이터
 * @param type ID or SNS 이름
 */
export const loginEventSend = async (data: Record<string, any>, route: RouteType | 'none'): Promise<void> => {
  // Amplitude
  amplitudeSetUserId(data.userId);
  airbridgeSetFieldUser(data.userId);
  //whatap
  window?.WhatapBrowserAgent?.setUserID?.(data?.userId);
  
  const applyDate = toFormatDate(new Date(), "YYYY-MM-DD");
  const eventProps = {
    type : route !== 'none' ? SNS.ROUTE_NAME[route] : 'ID 로그인',
  };
  
  const userProps = {
    last_login_date: applyDate,
    membership_level: data.groupName,
  };
  
  amplitudeSendEvent("complete_login", eventProps, userProps);
  
  await flareLaneSetUserId(data.memberSeq);
  await flareLaneTrackEvent('complete_login', eventProps);
  await flareLaneSetTag({ last_login_date : applyDate });
  
  airbridgeSendEvent('airbridge.user.signin'
    , route === 'none' ? 'generic' : route
    , route === 'none' ? 'generic_login' : 'social_login'
  );
};